import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { apiPost, setToken, getToken, setEntityId } from '../api/client';

const AUTH_STATE_KEY = 'tesoreria_auth_state';
const ENTITIES_KEY = 'tesoreria_entities';
const VIEW_ALL_KEY = 'tesoreria_view_all_entities';

export type EntityPerm = { entity_id: number; role: string; entity_name: string };

type AuthState = {
  token: string | null;
  userId: number | null;
  email: string | null;
  displayName: string | null;
  entities: EntityPerm[];
  scoutGroupId: number | null;
  scoutGroupName: string | null;
};

const defaultState: AuthState = {
  token: null,
  userId: null,
  email: null,
  displayName: null,
  entities: [],
  scoutGroupId: null,
  scoutGroupName: null,
};

type AuthContextValue = AuthState & {
  login: (username: string, password: string) => Promise<EntityPerm[]>;
  logout: () => void;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const t = getToken();
    if (t) {
      try {
        const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(AUTH_STATE_KEY) : null;
        if (stored) {
          const parsed = JSON.parse(stored) as AuthState;
          if (parsed.token === t) return { ...defaultState, ...parsed };
        }
        const entitiesStr = typeof localStorage !== 'undefined' ? localStorage.getItem(ENTITIES_KEY) : null;
        const entities: EntityPerm[] = entitiesStr ? (() => { try { return JSON.parse(entitiesStr); } catch { return []; } })() : [];
        const payload = JSON.parse(atob(t.split('.')[1] || '{}'));
        return {
          ...defaultState,
          token: t,
          userId: payload.sub ? Number(payload.sub) : null,
          email: payload.email || null,
          displayName: null,
          entities,
          scoutGroupId: null,
          scoutGroupName: null,
        };
      } catch {
        setToken(null);
      }
    }
    return defaultState;
  });

  useEffect(() => {
    if (state.token && state.entities.length >= 0 && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(AUTH_STATE_KEY, JSON.stringify(state));
    }
  }, [state.token, state.entities, state.userId, state.email, state.displayName, state.scoutGroupId, state.scoutGroupName]);

  const login = useCallback(async (username: string, password: string) => {
    const res = await apiPost<{
      access_token: string;
      user_id: number;
      email: string;
      display_name: string | null;
      entities: EntityPerm[];
      scout_group_id?: number | null;
      scout_group_name?: string | null;
    }>('/api/v1/auth/login', { username, password });
    setToken(res.access_token);
    const entitiesList = res.entities ?? [];
    setState({
      token: res.access_token,
      userId: res.user_id,
      email: res.email,
      displayName: res.display_name ?? null,
      entities: entitiesList,
      scoutGroupId: res.scout_group_id ?? null,
      scoutGroupName: res.scout_group_name ?? null,
    });
    if (typeof localStorage !== 'undefined') localStorage.setItem(ENTITIES_KEY, JSON.stringify(entitiesList));
    if (typeof sessionStorage !== 'undefined') {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith('tesoreria_welcome_'))
        .forEach((k) => sessionStorage.removeItem(k));
    }
    return entitiesList;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setEntityId(null);
    setState(defaultState);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(ENTITIES_KEY);
      localStorage.removeItem(VIEW_ALL_KEY);
    }
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(AUTH_STATE_KEY);
  }, []);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    isAuthenticated: !!state.token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
